"use client";

import type { ReactNode } from "react";
import { cn } from "../../../lib";
import {
	InteractiveAudioPlayerControlled,
	type InteractiveAudioPlayerControlledProps,
} from "./interactive-audio-player-controlled";
import { InteractiveAudioTransportOrb } from "./interactive-audio-transport-orb";
import { InteractiveAudioWaveformTrack } from "./interactive-audio-waveform-track";
import type { UseInteractiveAudioTransportResult } from "./use-interactive-audio-transport";

interface InteractiveAudioPlayerSelfContainedProps {
	mode: "self-contained";
	transport: UseInteractiveAudioTransportResult;
	waveformSeed: string;
	showLeadingWaveform?: boolean;
	volumePercent?: number;
	orbHue: number;
	volumeControl?: ReactNode;
	className?: string;
	playButtonClassName?: string;
	playAriaLabel?: string;
	pauseAriaLabel?: string;
	replayAriaLabel?: string;
}

export type InteractiveAudioPlayerProps =
	| InteractiveAudioPlayerSelfContainedProps
	| InteractiveAudioPlayerControlledProps;

export function InteractiveAudioPlayer(props: InteractiveAudioPlayerProps) {
	if (props.mode === "controlled") {
		return <InteractiveAudioPlayerControlled {...props} />;
	}

	const {
		mode,
		transport,
		waveformSeed,
		showLeadingWaveform = false,
		volumePercent,
		orbHue,
		volumeControl,
		className,
		playButtonClassName,
		playAriaLabel,
		pauseAriaLabel,
		replayAriaLabel,
	} = props;

	if (mode !== "self-contained") {
		return null;
	}

	const isBuffering = transport.transportState === "loading";

	return (
		<>
			<audio ref={transport.audioRef} className="hidden" preload="auto">
				<track kind="captions" />
			</audio>
			<div
				className={cn(
					"flex items-center gap-1",
					showLeadingWaveform && "w-full min-w-0",
					className,
				)}
			>
				{showLeadingWaveform ? (
					<InteractiveAudioWaveformTrack
						audioRef={transport.audioRef}
						waveformTimeline={transport.waveformTimeline}
						isActive={transport.isSessionActive}
						isPlaying={transport.transportState === "playing"}
						isPreparingPlayback={transport.isPreparingPlayback}
						isLoading={isBuffering}
						progressPercent={transport.displayProgressPercent}
						displayRevealPercent={transport.displayRevealPercent}
						bufferRevealSessionKey={
							transport.bufferRevealSessionKey
						}
						bufferRevealSessionStartMs={
							transport.bufferRevealSessionStartMs
						}
						waveformSeed={waveformSeed}
						volumePercent={volumePercent}
						onScrubPause={transport.pauseForScrub}
						onSeekCommit={transport.seekToPercent}
					/>
				) : null}
				<div
					className={cn(
						"flex shrink-0 items-center gap-1",
						showLeadingWaveform && "ml-auto",
					)}
				>
					{transport.isSessionActive && volumeControl != null
						? volumeControl
						: null}
					<InteractiveAudioTransportOrb
						transportState={transport.transportState}
						isSessionActive={transport.isSessionActive}
						isAtTrackEnd={transport.isAtTrackEnd}
						orbHue={orbHue}
						className={playButtonClassName}
						playAriaLabel={playAriaLabel}
						pauseAriaLabel={pauseAriaLabel}
						replayAriaLabel={replayAriaLabel}
						onTogglePlayback={transport.togglePlayback}
						onReplay={transport.replay}
					/>
				</div>
			</div>
		</>
	);
}
